import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../config';
import './LowLevelStocks.css';
import { FaArrowLeft } from 'react-icons/fa';
import RefreshButton from './RefreshButton';
import WatchlistStar from './WatchlistStar';

const LowLevelStocks = ({ onBack }) => {
  const [dayLowStocks, setDayLowStocks] = useState([]);
  const [dayHighStocks, setDayHighStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('dayLow'); // 'dayLow' or 'dayHigh'

  const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetch(`${API_BASE_URL}/top-low-level`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ category: 'fo' }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `HTTP ${response.status}`);
        }

        const data = await response.json();
        
        if (!data || typeof data !== 'object') {
          throw new Error('Invalid response format from server');
        }

        if (data.error) {
          throw new Error(data.error);
        }

        // Process day low stocks (negative percentage change)
        const dayLow = Array.isArray(data.low_performers) 
          ? data.low_performers
              .filter(item => item.percentage_change < 0)
              .map((item) => ({
                id: item.rank_near_low,
                name: item.stock || '—',
                ltp: item.ltp ? Number(item.ltp).toFixed(2) : '—',
                change: `${Number(item.percentage_change).toFixed(2)}%`,
                chart: '📉',
                highlight: item.rank_near_low === 1,
              }))
          : [];

        // Process day high stocks (positive percentage change from top_performers)
        const dayHigh = Array.isArray(data.top_performers)
          ? data.top_performers
              .filter(item => item.percentage_change > 0)
              .map((item) => ({
                id: item.rank_near_high || item.rank || '—',
                name: item.stock || '—',
                ltp: item.ltp ? Number(item.ltp).toFixed(2) : '—',
                change: `+${Number(item.percentage_change).toFixed(2)}%`,
                chart: '📈',
                highlight: (item.rank_near_high === 1) || (item.rank === 1),
              }))
          : [];

        setDayLowStocks(dayLow);
        setDayHighStocks(dayHigh);
        
        console.log('API Response:', data);
        console.log('Day Low Stocks:', dayLow);
        console.log('Day High Stocks:', dayHigh);
        
        if (dayLow.length === 0 && dayHigh.length === 0) {
          setError('No stock data available');
        }
      } catch (err) {
        setError(err.message || 'Failed to load data');
          // Clear both lists on error
          setDayLowStocks([]);
          setDayHighStocks([]);
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
    fetchData();
  }, []);

  const renderSkeletonRows = () => (
    <div className="stocks-table-container">
      <table className="stocks-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Name</th>
            <th>LTP</th>
            <th>Change</th>
            <th>SIGNALS</th>
          </tr>
        </thead>
      </table>
      <div className="loading-container">
        <div className="loading-circle"></div>
      </div>
    </div>
  );

  return (
    <div className="vb-page">
      <header className="momentum-header">
        <div className="momentum-header-container">
          <div className="logo">
            <img src="images/Final Font Stocks4U1.png" className="logo-text" alt="Stocks4U" />
          </div>
          <div className="momentum-user">
            <RefreshButton 
              onRefresh={fetchData} 
              isLoading={loading}
              size="small"
              style={{ marginRight: '10px' }}
            />
            <button className="momentum-back-btn" onClick={onBack}>← Back to Dashboard</button>
          </div>
        </div>
      </header>
      
      <div className="main-content">
        <div className="tabs">
          <button 
            className={`tab-button ${activeTab === 'dayLow' ? 'active' : ''}`}
            onClick={() => setActiveTab('dayLow')}
          >
            Day Low Stocks
          </button>
          <button 
            className={`tab-button ${activeTab === 'dayHigh' ? 'active' : ''}`}
            onClick={() => setActiveTab('dayHigh')}
          >
            Day High Stocks
          </button>
        </div>
        
        {loading ? (
          renderSkeletonRows()
        ) : error ? (
          <div className="error">{error}</div>
        ) : (
          <div className="stocks-table-container">
            <table className="stocks-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>LTP</th>
                  <th>Change</th>
                  <th>SIGNALS</th>
                </tr>
              </thead>
              <tbody>
                {(activeTab === 'dayLow' ? dayLowStocks : dayHighStocks).map((stock) => {
                  const isPositive = stock.change && !stock.change.includes('-');
                  return (
                    <tr key={`${activeTab}-${stock.id}`} className={stock.highlight ? 'highlight' : ''}>
                      <td>{stock.id}</td>
                      <td>
                        <WatchlistStar 
                          stock={{
                            symbol: stock.name,
                            name: stock.name,
                            ltp: stock.ltp,
                            change: stock.change
                          }} 
                          size="16px"
                        />
                        {stock.name}
                      </td>
                      <td>{stock.ltp}</td>
                      <td className={isPositive ? 'positive' : 'negative'}>
                        {stock.change}
                      </td>
                      <td style={{ color: isPositive ? '#10b981' : '#ef4444', fontSize: '1.2rem', fontWeight: 'bold' }}>
                        {isPositive ? '↑' : '↓'}
                      </td>
                    </tr>
                  );
                })}
                {!dayLowStocks.length && !dayHighStocks.length && (
                  <tr>
                    <td colSpan="5" className="empty-state">No stock data available</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default LowLevelStocks;
